import React from "react";
import Checkbox from "@source/components/UI/buttons/checkbox/checkbox";
import Radio from "@source/components/UI/buttons/radio/radio";
import DatePicker from "@source/components/UI/inputs/datePicker/datePicker";
import DateTimePicker from "@source/components/UI/inputs/dateTimePicker/dateTimePicker";
import TextInput from "@source/components/UI/inputs/textInput/textInput";
import {ConsentTemplateInput, ConsentTemplateInputType} from "@source/api/models/consentModels";
import styles from "./consentSteps.module.scss";

const isoDayToDate = (value: unknown) => {
  if (typeof value !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
};

const isoDateTimeToDate = (value: unknown) => {
  if (typeof value !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]));
  return Number.isNaN(date.getTime()) ? null : date;
};

const dateToIsoDay = (date: Date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const dateToIsoDateTime = (date: Date) => {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${dateToIsoDay(date)}T${hours}:${minutes}`;
};

export const TemplateInput = ({input, value, disabled, error, onChange}: {
  input: ConsentTemplateInput;
  value: unknown;
  disabled: boolean;
  error?: string;
  onChange: (value: unknown) => void;
}) => {
  const fieldId = `consent-field-${input.id}`;
  if (input.type === ConsentTemplateInputType.DATE) {
    return <div id={fieldId}>
      <DatePicker
        id={`consent-input-${input.id}`}
        className={styles.formControl}
        label={input.label}
        value={isoDayToDate(value)}
        disabled={disabled}
        error={error}
        touched={Boolean(error)}
        placeholder="DD/MM/YYYY"
        onChange={(date) => onChange(date ? dateToIsoDay(date) : "")}
      />
    </div>;
  }
  if (input.type === ConsentTemplateInputType.DATETIME) {
    return <div id={fieldId}>
      <DateTimePicker
        id={`consent-input-${input.id}`}
        className={styles.formControl}
        label={input.label}
        value={isoDateTimeToDate(value)}
        disabled={disabled}
        error={error}
        touched={Boolean(error)}
        placeholder="DD/MM/YYYY h:mm AM/PM"
        onChange={(date) => onChange(date ? dateToIsoDateTime(date) : "")}
      />
    </div>;
  }
  if (input.type === ConsentTemplateInputType.TEXT) {
    return <div id={fieldId}>
      <TextInput
        id={`consent-input-${input.id}`}
        className={`${styles.formControl} ${styles.templateTextInput}`}
        label={input.label}
        value={typeof value === "string" ? value : ""}
        multiline={input.multiline}
        disabled={disabled}
        error={error}
        touched={Boolean(error)}
        handleChange={(event) => onChange(event.target.value)}
      />
    </div>;
  }
  if (input.type === ConsentTemplateInputType.CHECKBOX) {
    return <div id={fieldId}>
      <Checkbox
        className={styles.templateBooleanInput}
        name={`consent-input-${input.id}`}
        value={input.id}
        checked={value === true}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        label={input.label}
      />
    </div>;
  }
  if (input.type === ConsentTemplateInputType.RADIO) {
    return <div id={fieldId} className={styles.inputGroup}>
      <strong>{input.label}</strong>
      {(input.options || []).map((option) => <Radio
        key={option.id}
        name={`consent-input-${input.id}`}
        value={option.id}
        checked={value === option.id}
        disabled={disabled}
        onChange={() => onChange(option.id)}
        label={option.label}
      />)}
      {error ? <p className={styles.fieldError}>{error}</p> : null}
    </div>;
  }
  const checked = Array.isArray(value) ? value : [];
  return <div id={fieldId} className={styles.inputGroup}>
    <strong>{input.label}</strong>
    {(input.options || []).map((option) => <Checkbox
      key={option.id}
      name={`consent-input-${input.id}-${option.id}`}
      value={option.id}
      checked={checked.includes(option.id)}
      disabled={disabled}
      onChange={() => onChange(checked.includes(option.id) ? checked.filter((id: string) => id !== option.id) : [...checked, option.id])}
      label={option.label}
    />)}
  </div>;
};
